import type { Scale } from '@tunasong/schemas'
import type { Chord } from '../chord/index.js'
import { getBorrowedFunction } from './borrowing.js'

/**
 * Helpers for the multi-chord context window (thread B-v2). Callers pass
 * `AnalysisContext.progression = { chords, currentIndex }`; these utilities
 * inspect a sliding window around `currentIndex` to inform confidence verdicts.
 *
 * v2 currently uses a single helper — `isIsolatedBorrowing` — but the module
 * exists so future windowed rules (approach-chord patterns, key-tracking) can
 * share the same window primitive without duplicating bounds-checking logic.
 */

/** Half-width of the sliding window. ±2 chords ≈ a 5-chord context. */
const WINDOW_HALF_WIDTH = 2

/**
 * Read the chords surrounding `currentIndex` (exclusive of the current chord).
 * Bounds-safe — clamps to the array's range.
 */
const getWindowChords = (chords: Chord[], currentIndex: number): Chord[] => {
  const start = Math.max(0, currentIndex - WINDOW_HALF_WIDTH)
  const end = Math.min(chords.length, currentIndex + WINDOW_HALF_WIDTH + 1)
  const window: Chord[] = []
  for (let i = start; i < end; i++) {
    if (i === currentIndex) {
      continue
    }
    window.push(chords[i])
  }
  return window
}

/**
 * Does the current chord look like an isolated parallel-mode borrowing in its
 * progression context? Returns true when:
 *
 * 1. The current chord is itself a parallel-mode borrowing (caller's signal),
 * 2. AND no other chord in the ±2 window is also a parallel-mode borrowing.
 *
 * A lone ♭VII surrounded by diatonic chords (e.g. C–Am–Bb–F) reads as a
 * chromatic outlier rather than a confident borrowing → downgrade to `low`.
 * The same ♭VII alongside other parallel-mode chords (e.g. Eb–Ab–Bb–C) reads
 * as a consistent borrowing pattern → keep at `medium`.
 *
 * The caller is responsible for confirming the current chord is a borrowing —
 * this helper only inspects the surrounding window. We accept the current
 * chord's borrowing status as a precondition so we don't re-run analysis on
 * it; the analyzer already knows.
 */
export const isIsolatedBorrowing = (chords: Chord[], currentIndex: number, scale: Scale): boolean => {
  const window = getWindowChords(chords, currentIndex)
  for (const neighbor of window) {
    if (getBorrowedFunction(neighbor, scale)) {
      return false
    }
  }
  return true
}
